import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { doc, updateDoc } from "firebase/firestore";
import { convertToDisplayDate, toDdMmYyyy } from "../../utils/date";
import { SPM_SUBJECTS } from "../../Data/subjectData";
import { db } from "../../firebase";
import { showErrorToast, showSuccessToast } from "../../toastmsg";

const value = (input: unknown): string => {
  if (input == null || input === "") return "-";
  if (Array.isArray(input)) return input.filter(Boolean).join(", ") || "-";
  if (
    typeof input === "object" &&
    "toDate" in input &&
    typeof (input as any).toDate === "function"
  ) {
    const date = (input as any).toDate();
    return toDdMmYyyy(date) || String(date);
  }
  if (input instanceof Date) return toDdMmYyyy(input) || input.toISOString();
  return String(input);
};
const list = (input: unknown): string[] =>
  Array.isArray(input)
    ? input.filter(Boolean).map(String)
    : typeof input === "string" && input.trim()
      ? input
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

export default function SpmRevisionClassSection({
  lead,
  onSubjectsSaved,
}: {
  lead: Record<string, any>;
  onSubjectsSaved?: (subjects: string[]) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const subjects = list(lead.SpmTuition?.subject);
  const [draftSubjects, setDraftSubjects] = useState(subjects);

  useEffect(() => {
    if (!isEditing) setDraftSubjects(subjects);
  }, [lead.SpmTuition?.subject, isEditing]);

  const saveSubjects = async () => {
    const leadId = lead.ID || lead.id;
    if (!leadId) {
      showErrorToast("Lead ID is missing");
      return;
    }

    try {
      setIsSaving(true);
      await updateDoc(doc(db, "leads", leadId), {
        "SpmTuition.subject": draftSubjects,
      });
      onSubjectsSaved?.(draftSubjects);
      setIsEditing(false);
      showSuccessToast("SPM subjects updated successfully");
    } catch (error: any) {
      console.error("Failed to update SPM subjects:", error);
      showErrorToast(error.message || "Failed to update SPM subjects.");
    } finally {
      setIsSaving(false);
    }
  };
  const visible =
    lead.campaign === "SPM Ultimate Revision Class 2026" ||
    Boolean(lead.SpmTuition);
  if (!visible) return null;
  const fields: [string, unknown][] = [
    // ["English Name", lead.name],
    // ["School Name", lead.schoolName],
    ["Identity", lead.identity],
    // ["Country", lead.country],
    // ["Country Code", lead.countryCode],
    // ["Phone Number", lead.phoneNumber],
    // ["Email", lead.email],
    ["Discovery Source", lead.discoverySource],
    ["Qualification", lead.SpmTuition?.qualification],
  ];
  return (
    <div className={`rounded-md border border-teal-200 bg-teal-50 p-2`}>
      <button
        type="button"
        onClick={() => setExpanded((item) => !item)}
        className="flex w-full items-center justify-between text-left font-bold text-teal-800"
      >
        <span>SPM Ultimate Revision Class</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <div className="mt-2 space-y-1 text-teal-900">
          {fields.map(([label, fieldValue]) => (
            <p key={label}>
              <span className="font-semibold">{label}:</span>{" "}
              {value(fieldValue)}
            </p>
          ))}
          <div className="pt-2">
            <div className="flex items-center justify-between">
              <p className="font-semibold">Subjects:</p>
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setDraftSubjects(subjects);
                    setIsEditing(true);
                  }}
                  className="text-sm text-blue-600 underline"
                >
                  Edit
                </button>
              )}
            </div>
            {isEditing ? (
              <div className="mt-2">
                <div className="grid grid-cols-2 gap-2">
                  {SPM_SUBJECTS.map((option) => (
                    <label
                      key={option.value}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={draftSubjects.includes(option.value)}
                        onChange={() =>
                          setDraftSubjects((current) =>
                            current.includes(option.value)
                              ? current.filter(
                                  (subject) => subject !== option.value,
                                )
                              : [...current, option.value],
                          )
                        }
                        className="h-4 w-4 accent-teal-700"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={saveSubjects}
                    disabled={isSaving}
                    className="rounded bg-blue-600 px-3 py-1 text-white hover:bg-blue-700 disabled:bg-blue-300"
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDraftSubjects(subjects);
                      setIsEditing(false);
                    }}
                    disabled={isSaving}
                    className="rounded bg-gray-500 px-3 py-1 text-white hover:bg-gray-600 disabled:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : subjects.length ? (
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {subjects.map((subject, index) => (
                  <li key={`${subject}-${index}`}>
                    <span className="font-medium">{subject}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>-</p>
            )}
          </div>
          {/* {[
            ["Status", lead.status],
            ["Lead Source", lead.leadSource],
            ["Campaign", lead.campaign],
          ].map(([label, fieldValue]) => (
            <p key={String(label)}>
              <span className="font-semibold">{label}:</span>{" "}
              {value(fieldValue)}
            </p>
          ))} */}
          {[
            // ["Last Updated At", lead.tuition?.updatedAt],
            ["Account Status", lead.accountStatus],
            ["Portal Access", lead.portalAccess ? "Yes" : "-"],
            ["Portal Provisioning", lead.portalProvisioningStatus],
            ["Password Setup", lead.portalPasswordSetup],
            // ["Student UID", lead.studentUID],
            ["Portal Provisioned At", lead.portalProvisionedAt],
          ].map(([label, fieldValue]) => (
            <p key={String(label)}>
              <span className="font-semibold">{label}:</span>{" "}
              {value(fieldValue)}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
